import { useEffect, useMemo, useState, useCallback, useRef } from 'react';

import useNavigateWithSearch from '../../../../hooks/useNavigateWithSearch';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import { formatCurrencyNumber, formatQuantity } from '../../utils/numbers';
import useScrollToTop from '../../utils/hooks/useScrollToTop';
import LoadingBar from '../../global/LoadingBar';
import {
  isNotificationInboxWebPath,
  ROUTE_GIFT_CARDS_V2,
  ROUTE_LOGOUT,
  ROUTE_MANAGE_MEMBERSHIP,
  ROUTE_MEMBERSHIP_ADDRESSES,
  ROUTE_MEMBERSHIP_ORDERS,
  ROUTE_SETTINGS,
  ROUTE_TAKE_BACK_BAGS,
  ROUTE_TRANSACTIONS,
  ROUTE_WALLET,
} from '../../config/routes';
import { useAlert } from '../../../providers/AlertProvider';
import { FAQS_URL, HELP_URL } from '../../config/externalURL';
import { getMilestoneProgress, getSustainabilityImpactUser } from '../../utils/service';
import HelpCircleIcon from '../../images/help-circle.svg';
import BadgeInfoIcon from '../../images/badge-info.svg';
import QRIcon from '../../images/QR.svg';
import ArrowUpIcon from '../../images/arrow-left-right-up.svg';
import CHEVRON_RIGHT_FIGMA_ICON from '../../images/chevron-right-figma.svg';
import EXTERNAL_LINK_FIGMA_ICON from '../../images/external-link-figma.svg';
import ShareIcon from '../../images/share-figma.svg';
import TransfersIcon from '../../images/arrow-left-right.svg';
import SettingsIcon from '../../images/settings-user-figma.svg';
import CreditCardIcon from '../../images/credit-card-icon.svg';
import { ReactComponent as TransactionsIcon } from '../../images/transactions-figma.svg';
import TCBalanceIcon from '../../images/tc-balance-figma.svg';
import NotificationBellIcon from '../../images/notification-bell-custom.svg';
import NotificationsNavIcon from '../../images/notifications.svg';
import NotificationsNavIconUnread from '../../images/notifications-full.svg';
import ShoppingCartIcon from '../../images/shopping-cart-figma.svg';
import BackArrowIcon from '../../images/back-arrow.svg';
import TelescopeIcon from '../../images/telescope.svg';
import UnlimitedBadgeIcon from '../../images/unlimited-badge-figma.svg';
import MembershipActivityIcon from '../../images/membership-activity-figma.svg';
import DeliveryAddressIcon from '../../images/delivery-address-figma.svg';
import ManageMembershipIcon from '../../images/manage-membership-figma.svg';
import useShareCardAsImage from '../../utils/hooks/useShareCardAsImage';
import getHeroScrollState, { getHeroSnapTarget } from './heroScrollState';
import getActiveMilestoneRange from './milestoneProgressRange';
import MilestoneProgressBar from './MilestoneProgressBar';
import { PackageTrackingPill } from '../../global/PackageTrackingPill/PackageTrackingPill';
import ACCOUNT_DRAWER_OPEN_EVENT, {
  ACCOUNT_DRAWER_NOTIFICATIONS_TAB_SESSION_KEY,
} from '../../config/events';
import './mockMilestone';

import {
  fetchAppNotifications,
  getAppNotificationDeeplink,
  normalizeAppNotification,
  NOTIFICATIONS_PAGE_SIZE,
  NotificationStatus,
  NotificationSection,
  splitNotificationDeeplinkForNavigation,
  updateAppNotificationStatus,
} from '../../utils/appNotificationsApi';
import getRelativeNotificationTime from '../../utils/notificationTime';

import './MyAccount.scss';

const MY_STUFF_PRIMARY = [
  {
    label: 'Shipping labels',
    route: ROUTE_TAKE_BACK_BAGS,
    icon: QRIcon,
    title: 'Shipping labels',
  },
  {
    label: 'Gift cards',
    route: ROUTE_GIFT_CARDS_V2,
    icon: CreditCardIcon,
    title: 'Gift cards',
  },
];

const MY_STUFF_SECONDARY = [
  {
    label: 'View your TrashieCash activity',
    route: ROUTE_TRANSACTIONS,
    iconComponent: TransactionsIcon,
    title: 'Transactions',
  },
  {
    label: 'Transfer your balance',
    route: ROUTE_WALLET,
    icon: TransfersIcon,
    title: 'Transfers',
  },
  {
    label: 'Update your account preferences',
    route: ROUTE_SETTINGS,
    icon: SettingsIcon,
    title: 'Settings',
  },
];

const SUPPORT_ITEMS = [
  {
    label: 'Quick answers to common questions',
    route: FAQS_URL,
    external: true,
    icon: HelpCircleIcon,
    title: 'FAQs',
  },
  {
    label: 'Detailed information',
    route: HELP_URL,
    external: true,
    icon: BadgeInfoIcon,
    title: 'Help Center',
  },
];

const MEMBERSHIP_ITEMS = [
  {
    label: 'Review your membership orders',
    route: ROUTE_MEMBERSHIP_ORDERS,
    icon: MembershipActivityIcon,
    title: 'Membership Activity',
  },
  {
    label: 'Update shipping destination',
    route: ROUTE_MEMBERSHIP_ADDRESSES,
    icon: DeliveryAddressIcon,
    title: 'Delivery Address',
  },
  {
    label: 'Manage your plan settings',
    route: ROUTE_MANAGE_MEMBERSHIP,
    icon: ManageMembershipIcon,
    title: 'Manage Membership',
  },
];

const ACCOUNT_ITEMS = [
  {
    label: 'Sign out of your account',
    route: ROUTE_LOGOUT,
    icon: ArrowUpIcon,
    title: 'Logout',
  },
];

const NOTIFICATIONS_FILTER_MESSAGES = 'messages';
const NOTIFICATIONS_FILTER_NEWS = 'news';

/** Matches trashie-mobile `NotificationType.REWARD` / image aspect handling. */
const NOTIFICATION_TYPE_REWARD = 'reward';

const MAX_NOTIFICATION_AUTO_FETCH_PAGES = 5;

const asArray = (value) => (Array.isArray(value) ? value : []);

const toFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getActiveMonths = (userData) => {
  const sourceDate = userData?.memberSince;

  if (sourceDate == null) {
    return null;
  }

  let startDate;
  if (typeof sourceDate === 'object' && sourceDate !== null && '_seconds' in sourceDate) {
    startDate = new Date(sourceDate._seconds * 1000);
  } else {
    startDate = new Date(sourceDate);
  }
  if (Number.isNaN(startDate.getTime())) {
    return null;
  }

  const today = new Date();
  const months = ((today.getFullYear() - startDate.getFullYear()) * 12)
    + (today.getMonth() - startDate.getMonth());

  return Math.max(1, Number(months));
};

const IMPACT_DEFAULT = {
  landfillDiversion: 0,
  co2EmissionDiversion: 0,
  waterSaved: 0,
};
const IMPACT_EMPTY_STATE = {
  landfill: {
    title: 'Get Started',
    label: 'Divert landfill waste when recycling with Trashie',
  },
  co2: {
    title: "Don't Wait",
    label: 'Divert CO2 emissions too!',
  },
  water: {
    title: 'Act Now',
    label: 'Water will be saved as well',
  },
};
const PRODUCT_TYPE_LABELS = {
  TEXTIL: { singular: 'bag', plural: 'bags' },
  TECH: { singular: 'box', plural: 'boxes' },
  VIRTUAL: { singular: 'virtual box', plural: 'virtual boxes' },
};
const PRODUCT_TYPE_ALIASES = {
  TEXTIL: 'TEXTIL',
  TEXTILES_TBB: 'TEXTIL',
  TEXTILE_TBB: 'TEXTIL',
  TECH: 'TECH',
  TECH_BOX: 'TECH',
  VIRTUAL: 'VIRTUAL',
  VIRTUAL_BOX: 'VIRTUAL',
};

const formatCleanOutsSubtitle = (productCounts = []) => productCounts
  .map(({ recyclingProductTypeId, count }) => ({
    recyclingProductTypeId,
    count: toFiniteNumber(count, 0),
  }))
  .filter(({ count }) => count > 0)
  .map(({ recyclingProductTypeId, count }) => {
    const normalizedProductTypeId = PRODUCT_TYPE_ALIASES[recyclingProductTypeId] ||
      recyclingProductTypeId;
    const labels = PRODUCT_TYPE_LABELS[normalizedProductTypeId];
    if (!labels) {
      return `${count} ${recyclingProductTypeId}`;
    }

    return `${count} ${count === 1 ? labels.singular : labels.plural}`;
  })
  .join('  |  ');

const getMilestoneMockState = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.__trashieMilestoneMockState || null;
};

const buildNextMilestoneFromThresholds = (thresholds = [], currentBag = 0, existingNext = null) => {
  const sortedThresholds = [...asArray(thresholds)].sort(
    (a, b) => Number(a?.bagNumber) - Number(b?.bagNumber),
  );
  const nextThreshold = sortedThresholds.find(
    ({ bagNumber }) => Number(bagNumber) > currentBag,
  );

  if (!nextThreshold) {
    return null;
  }

  return {
    ...(existingNext || {}),
    bagNumber: Number(nextThreshold.bagNumber),
    bonusAmount: Number(nextThreshold.bonusAmount),
    remainingBags: Math.max(Number(nextThreshold.bagNumber) - currentBag, 0),
  };
};

const applyMilestoneMockState = (milestoneData) => {
  const mockState = getMilestoneMockState();
  if (
    !mockState?.enabled
    || !Array.isArray(milestoneData?.milestoneThresholds)
  ) {
    return milestoneData;
  }

  const currentBag = Math.max(0, Number(mockState.bagNumber) || 0);
  return {
    ...milestoneData,
    currentBagNumberInCycle: currentBag,
    nextMilestone: buildNextMilestoneFromThresholds(
      milestoneData.milestoneThresholds,
      currentBag,
      milestoneData.nextMilestone,
    ),
  };
};

const HERO_COLLAPSE_SCROLL_DISTANCE = 120;
const HERO_COLLAPSE_MAX_PROGRESS = 0.88;
const HERO_SNAP_SETTLE_DELAY_MS = 110;
const HERO_SNAP_RELEASE_DELAY_MS = 550;

const DRAWER_TAB_ACCOUNT = 'account';
const DRAWER_TAB_NOTIFICATIONS = 'notifications';

/** In-app notifications drawer: limit to internal emails until GA (prod testing). */
const NOTIFICATIONS_DRAWER_ADMIN_EMAIL_SUFFIXES = ['@fordays.com', '@trashie.io'];

function isNotificationsDrawerAdminEmail(email) {
  if (!email || typeof email !== 'string') {
    return false;
  }
  const normalized = email.trim().toLowerCase();
  return NOTIFICATIONS_DRAWER_ADMIN_EMAIL_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
}

const MyAccount = ({ isDrawer = false, onClose }) => {
  useScrollToTop();
  const { userData, user, isLoading: isLoadingFirebaseAuth } = useFirebaseAuth();
  const showNotificationsDrawerFeatures = useMemo(
    () => isNotificationsDrawerAdminEmail(userData?.email),
    [userData?.email],
  );
  const navigateWithSearch = useNavigateWithSearch();
  const setAlert = useAlert();
  const [drawerTab, setDrawerTab] = useState(DRAWER_TAB_ACCOUNT);
  const [notificationsFilter, setNotificationsFilter] = useState(NOTIFICATIONS_FILTER_MESSAGES);
  const [notificationList, setNotificationList] = useState([]);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [notificationsHasMore, setNotificationsHasMore] = useState(true);
  const [notificationsFetching, setNotificationsFetching] = useState(false);
  const [notificationsError, setNotificationsError] = useState(null);
  const [notificationsAutoFetchCount, setNotificationsAutoFetchCount] = useState(0);
  const [unreadNotificationsTotal, setUnreadNotificationsTotal] = useState(0);
  const notificationsLoadMoreLockRef = useRef(false);
  const [impactValues, setImpactValues] = useState(IMPACT_DEFAULT);
  const [isLoadingImpact, setIsLoadingImpact] = useState(false);
  const [milestoneProgress, setMilestoneProgress] = useState(null);
  const [heroCollapseProgress, setHeroCollapseProgress] = useState(0);
  const [lockedContentOffset, setLockedContentOffset] = useState(0);
  const [isHeroCollapseEnabled, setIsHeroCollapseEnabled] = useState(true);
  const accountScrollRef = useRef(null);
  const heroContainerRef = useRef(null);
  const impactCardRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const isAutoSnappingRef = useRef(false);
  const snapSettleTimerRef = useRef(null);
  const snapReleaseTimerRef = useRef(null);
  const collapseAvailabilityFrameRef = useRef(null);
  const impactShareFileName = 'My Impact.png';
  const { share: handleShareImpact, isSharing: isSharingImpact } = useShareCardAsImage({
    cardRef: impactCardRef,
    shareButtonSelector: '.MyAccount__impact-share',
    fileName: impactShareFileName,
  });

  useEffect(() => {
    if (isLoadingFirebaseAuth) {
      return;
    }

    if (userData?.error) {
      setAlert(userData.error);
    }
  }, [userData?.error, isLoadingFirebaseAuth]);

  useEffect(() => {
    if (showNotificationsDrawerFeatures) {
      return undefined;
    }
    if (drawerTab === DRAWER_TAB_NOTIFICATIONS) {
      setDrawerTab(DRAWER_TAB_ACCOUNT);
    }
    return undefined;
  }, [showNotificationsDrawerFeatures, drawerTab]);

  const loadImpact = useCallback(async () => {
    setIsLoadingImpact(true);
    const { data, error } = await getSustainabilityImpactUser();
    if (error) {
      setAlert({
        type: 'alert',
        message: error.message || '',
        error,
      });
      setIsLoadingImpact(false);
      return;
    }

    const impactList = asArray(data?.impact);
    const getImpactQuantity = (id) => impactList.find((item) => item?.id === id)?.quantity ?? 0;

    setImpactValues({
      landfillDiversion: getImpactQuantity('landfillDiversion'),
      co2EmissionDiversion: getImpactQuantity('co2EmissionDiversion'),
      waterSaved: getImpactQuantity('waterSaved'),
    });
    setIsLoadingImpact(false);
  }, [setAlert]);

  const loadMilestoneProgress = useCallback(async () => {
    const { data, error } = await getMilestoneProgress();
    if (error) {
      return;
    }

    // Temporary debug log requested to inspect milestone payload from API.
    // eslint-disable-next-line no-console
    console.log('[MyAccount] milestone-progress API payload', data);

    const nextMilestoneData = applyMilestoneMockState(data || null);

    setMilestoneProgress(nextMilestoneData);
  }, []);

  useEffect(() => {
    if (!isLoadingFirebaseAuth && user) {
      loadImpact();
      loadMilestoneProgress();
    }
  }, [isLoadingFirebaseAuth, user, loadImpact, loadMilestoneProgress]);

  const refreshUnreadNotificationsTotal = useCallback(async () => {
    if (!isDrawer || !user || !showNotificationsDrawerFeatures) {
      return;
    }
    try {
      const { total } = await fetchAppNotifications({
        page: 1,
        pageSize: 1,
        status: NotificationStatus.UNREAD,
      });
      setUnreadNotificationsTotal(total);
    } catch {
      /* keep prior count */
    }
  }, [isDrawer, user, showNotificationsDrawerFeatures]);

  useEffect(() => {
    if (!isDrawer || !user || !showNotificationsDrawerFeatures) {
      return undefined;
    }
    if (drawerTab === DRAWER_TAB_ACCOUNT) {
      refreshUnreadNotificationsTotal();
    }
    return undefined;
  }, [isDrawer, user, drawerTab, refreshUnreadNotificationsTotal, showNotificationsDrawerFeatures]);

  useEffect(() => {
    if (!isDrawer || !showNotificationsDrawerFeatures) {
      return undefined;
    }
    try {
      const nextTab = sessionStorage.getItem(ACCOUNT_DRAWER_NOTIFICATIONS_TAB_SESSION_KEY);
      if (nextTab === DRAWER_TAB_NOTIFICATIONS) {
        sessionStorage.removeItem(ACCOUNT_DRAWER_NOTIFICATIONS_TAB_SESSION_KEY);
        setDrawerTab(DRAWER_TAB_NOTIFICATIONS);
      }
    } catch {
      /* ignore */
    }
    return undefined;
  }, [isDrawer, showNotificationsDrawerFeatures]);

  useEffect(() => {
    if (!isDrawer || !showNotificationsDrawerFeatures || drawerTab !== DRAWER_TAB_NOTIFICATIONS) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setNotificationsFetching(true);
      setNotificationsError(null);
      setNotificationsPage(1);
      setNotificationsAutoFetchCount(0);
      notificationsLoadMoreLockRef.current = false;
      try {
        const { notifications: batch } = await fetchAppNotifications({
          page: 1,
          pageSize: NOTIFICATIONS_PAGE_SIZE,
        });
        if (cancelled) return;
        const normalized = batch.map(normalizeAppNotification).filter(Boolean);
        setNotificationList(normalized);
        setNotificationsHasMore(batch.length === NOTIFICATIONS_PAGE_SIZE);
      } catch (e) {
        if (!cancelled) {
          setNotificationsError(e);
          setNotificationList([]);
        }
      } finally {
        if (!cancelled) setNotificationsFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDrawer, drawerTab, showNotificationsDrawerFeatures]);

  useEffect(() => {
    if (!isDrawer || !showNotificationsDrawerFeatures || drawerTab !== DRAWER_TAB_NOTIFICATIONS) {
      return undefined;
    }
    if (notificationsPage <= 1) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setNotificationsFetching(true);
      try {
        const { notifications: batch } = await fetchAppNotifications({
          page: notificationsPage,
          pageSize: NOTIFICATIONS_PAGE_SIZE,
        });
        if (cancelled) return;
        const normalized = batch.map(normalizeAppNotification).filter(Boolean);
        setNotificationList((prev) => {
          const existingUids = new Set(prev.map((n) => n.uid));
          const newItems = normalized.filter((n) => !existingUids.has(n.uid));
          return newItems.length > 0 ? [...prev, ...newItems] : prev;
        });
        setNotificationsHasMore(batch.length === NOTIFICATIONS_PAGE_SIZE);
        setNotificationsError(null);
      } catch (e) {
        if (!cancelled) setNotificationsError(e);
      } finally {
        if (!cancelled) {
          setNotificationsFetching(false);
          notificationsLoadMoreLockRef.current = false;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDrawer, drawerTab, notificationsPage, showNotificationsDrawerFeatures]);

  useEffect(() => {
    setNotificationsAutoFetchCount(0);
  }, [notificationsFilter]);

  const filteredNotifications = useMemo(() => {
    if (notificationsFilter === NOTIFICATIONS_FILTER_NEWS) {
      return notificationList.filter((n) => n?.data?.section === NotificationSection.NEWS);
    }
    return notificationList.filter((n) => n?.data?.section !== NotificationSection.NEWS);
  }, [notificationList, notificationsFilter]);

  const notificationsContent = useMemo(() => {
    const unread = filteredNotifications.filter((n) => n.status === NotificationStatus.UNREAD);
    const read = filteredNotifications.filter((n) => n.status === NotificationStatus.READ);
    return { unread, read };
  }, [filteredNotifications]);

  const willNotificationsAutoFetch = (
    filteredNotifications.length === 0
    && notificationList.length > 0
    && notificationsHasMore
    && !notificationsFetching
    && !notificationsError
    && notificationsAutoFetchCount < MAX_NOTIFICATION_AUTO_FETCH_PAGES
  );

  const showNotificationsSkeleton = (
    (notificationsFetching || willNotificationsAutoFetch)
    && filteredNotifications.length === 0
  );

  const showNotificationsEmpty = (
    !notificationsFetching
    && !willNotificationsAutoFetch
    && (notificationsError || (filteredNotifications.length === 0))
  );

  const hasUnreadNotifications = showNotificationsDrawerFeatures && unreadNotificationsTotal > 0;

  useEffect(() => {
    if (!isDrawer || !showNotificationsDrawerFeatures || drawerTab !== DRAWER_TAB_NOTIFICATIONS) {
      return undefined;
    }
    const willTry =
      filteredNotifications.length === 0
      && notificationList.length > 0
      && notificationsHasMore
      && !notificationsFetching
      && !notificationsError
      && notificationsAutoFetchCount < MAX_NOTIFICATION_AUTO_FETCH_PAGES;
    if (!willTry) {
      return undefined;
    }
    setNotificationsAutoFetchCount((c) => c + 1);
    if (!notificationsLoadMoreLockRef.current) {
      notificationsLoadMoreLockRef.current = true;
      setNotificationsPage((p) => p + 1);
    }
    return undefined;
  }, [
    isDrawer,
    drawerTab,
    filteredNotifications.length,
    notificationList.length,
    notificationsHasMore,
    notificationsFetching,
    notificationsError,
    notificationsAutoFetchCount,
    showNotificationsDrawerFeatures,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleMilestoneMockUpdated = () => {
      if (!isLoadingFirebaseAuth && user) {
        loadMilestoneProgress();
      }
    };

    window.addEventListener('trashie:milestone-mock-updated', handleMilestoneMockUpdated);
    return () => {
      window.removeEventListener('trashie:milestone-mock-updated', handleMilestoneMockUpdated);
    };
  }, [isLoadingFirebaseAuth, loadMilestoneProgress, user]);

  useEffect(() => () => {
    if (collapseAvailabilityFrameRef.current !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(collapseAvailabilityFrameRef.current);
      collapseAvailabilityFrameRef.current = null;
    }
    if (snapSettleTimerRef.current) {
      clearTimeout(snapSettleTimerRef.current);
      snapSettleTimerRef.current = null;
    }
    if (snapReleaseTimerRef.current) {
      clearTimeout(snapReleaseTimerRef.current);
      snapReleaseTimerRef.current = null;
    }
  }, []);

  const updateHeroCollapseAvailability = useCallback(() => {
    if (!isDrawer) {
      setIsHeroCollapseEnabled(true);
      return;
    }

    const scrollContainer = accountScrollRef.current;
    const heroContainer = heroContainerRef.current;
    if (!scrollContainer || !heroContainer) {
      return;
    }

    const viewportHeight = Math.max(Number(scrollContainer.clientHeight) || 0, 0);
    const heroHeight = Math.max(Number(heroContainer.offsetHeight) || 0, 0);
    if (viewportHeight <= 0) {
      return;
    }

    const maxScrollableTop = Math.max(
      (scrollContainer.scrollHeight ?? 0) - viewportHeight,
      0,
    );

    // Collapse only when hero is at least half of the drawer viewport height.
    const heroCollapseThreshold = viewportHeight * 0.5;
    const isHeroLargeEnoughToCollapse = heroHeight >= heroCollapseThreshold;
    const hasScrollableRunway = maxScrollableTop > 1;
    const nextEnabled = isHeroLargeEnoughToCollapse && hasScrollableRunway;
    setIsHeroCollapseEnabled((currentEnabled) => (
      currentEnabled === nextEnabled ? currentEnabled : nextEnabled
    ));
  }, [isDrawer]);

  const scheduleHeroCollapseAvailabilityUpdate = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (collapseAvailabilityFrameRef.current !== null) {
      window.cancelAnimationFrame(collapseAvailabilityFrameRef.current);
    }

    collapseAvailabilityFrameRef.current = window.requestAnimationFrame(() => {
      collapseAvailabilityFrameRef.current = null;
      updateHeroCollapseAvailability();
    });
  }, [updateHeroCollapseAvailability]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    scheduleHeroCollapseAvailabilityUpdate();
    window.addEventListener('resize', scheduleHeroCollapseAvailabilityUpdate);

    return () => {
      window.removeEventListener('resize', scheduleHeroCollapseAvailabilityUpdate);
      if (collapseAvailabilityFrameRef.current !== null) {
        window.cancelAnimationFrame(collapseAvailabilityFrameRef.current);
        collapseAvailabilityFrameRef.current = null;
      }
    };
  }, [scheduleHeroCollapseAvailabilityUpdate]);

  useEffect(() => {
    scheduleHeroCollapseAvailabilityUpdate();
  }, [
    scheduleHeroCollapseAvailabilityUpdate,
    isLoadingFirebaseAuth,
    isLoadingImpact,
    milestoneProgress,
    userData?.activeSubscription,
    userData?.firstName,
    userData?.lastName,
    userData?.email,
  ]);

  useEffect(() => {
    if (isHeroCollapseEnabled) {
      return;
    }

    isAutoSnappingRef.current = false;
    if (snapSettleTimerRef.current) {
      clearTimeout(snapSettleTimerRef.current);
      snapSettleTimerRef.current = null;
    }
    if (snapReleaseTimerRef.current) {
      clearTimeout(snapReleaseTimerRef.current);
      snapReleaseTimerRef.current = null;
    }

    setHeroCollapseProgress((currentProgress) => (currentProgress === 0 ? currentProgress : 0));
    setLockedContentOffset((currentOffset) => (currentOffset === 0 ? currentOffset : 0));
  }, [isHeroCollapseEnabled]);

  const activeMonths = useMemo(() => getActiveMonths(userData), [userData]);
  const accountBalance = toFiniteNumber(userData?.balance?.currentBalance, 0);
  const accountStatusLabel = userData?.activeSubscription ? 'Unlimited Member' : null;
  const firstName = (userData?.firstName || '').trim();
  const email = (userData?.email || '').trim();
  const isEmailFallbackUserTitle = !firstName && Boolean(email);
  const userDisplayName = firstName || email || 'Account';
  const renderedUserDisplayName = isEmailFallbackUserTitle ? email.toLowerCase() : userDisplayName;
  const profileInitial = (userDisplayName?.[0] || 'A').toUpperCase();
  const impactMetrics = useMemo(() => {
    const toMetric = (value = 0, unit = '') => {
      const numericValue = Number.isFinite(Number(value)) ? Number(value) : 0;
      const { value: compactValue, suffix } = formatQuantity(numericValue);
      return `${compactValue}${suffix || ''} ${unit}`.trim();
    };

    return [
      {
        key: 'landfill',
        label: 'Landfill Waste Saved',
        modifier: 'landfill',
        value: Number(impactValues.landfillDiversion) || 0,
        valueDisplay: toMetric(impactValues.landfillDiversion, 'lbs'),
      },
      {
        key: 'co2',
        label: 'Co2 Offset',
        modifier: 'co2',
        value: Number(impactValues.co2EmissionDiversion) || 0,
        valueDisplay: toMetric(impactValues.co2EmissionDiversion, 'lbs'),
      },
      {
        key: 'water',
        label: 'Water Saved',
        modifier: 'water',
        value: Number(impactValues.waterSaved) || 0,
        valueDisplay: toMetric(impactValues.waterSaved, 'gal.'),
      },
    ];
  }, [impactValues]);

  const milestoneThresholds = useMemo(
    () => [...asArray(milestoneProgress?.milestoneThresholds)].sort(
      (a, b) => Number(a?.bagNumber ?? 0) - Number(b?.bagNumber ?? 0),
    ),
    [milestoneProgress?.milestoneThresholds],
  );
  const currentBagNumberInCycle = toFiniteNumber(milestoneProgress?.currentBagNumberInCycle, 0);
  const milestoneTrackHorizontalPaddingPx = 32;
  const milestoneDotSizePx = 10;
  const milestoneNodeCenterInsetPx = milestoneTrackHorizontalPaddingPx + (milestoneDotSizePx / 2);
  const nextMilestone = milestoneProgress?.nextMilestone ?? null;
  const parsedNextMilestoneBagNumber = Number(nextMilestone?.bagNumber);
  const nextMilestoneBagNumber = Number.isFinite(parsedNextMilestoneBagNumber)
    ? parsedNextMilestoneBagNumber
    : null;
  const {
    allMilestonesCompleted,
    activeThresholdIndex,
    segmentStartBag,
    showPerBagRewardAmount,
    previousMilestoneBagNumber,
    previousMilestoneBonusAmount,
    targetMilestoneBagNumber,
    targetMilestoneBonusAmount,
    markerBagNumbers,
  } = useMemo(() => getActiveMilestoneRange({
    milestoneThresholds,
    currentBagNumberInCycle,
    nextMilestoneBagNumber,
  }), [currentBagNumberInCycle, milestoneThresholds, nextMilestoneBagNumber]);
  const lastMilestoneIndex = Math.max(milestoneThresholds.length - 1, 0);
  const nextBagNumber = currentBagNumberInCycle + 1;
  const nextBagThreshold = useMemo(
    () => milestoneThresholds.find((t) => Number(t?.bagNumber) === nextBagNumber),
    [milestoneThresholds, nextBagNumber],
  );
  const isNextBagAMilestone = nextBagThreshold != null;
  let milestoneCtaAmount = 2;
  if (isNextBagAMilestone) {
    const bonus = toFiniteNumber(nextBagThreshold.bonusAmount, 0);
    milestoneCtaAmount = nextBagNumber === 1 ? bonus : 2 + bonus;
  }
  const milestoneCtaLabel = isNextBagAMilestone ? 'Jackpot' : 'Milestone';
  const milestoneReturnOrdinal = (() => {
    const n = nextBagNumber % 100;
    const suffix = (n >= 11 && n <= 13) ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] || 'th';
    return `${nextBagNumber}${suffix}`;
  })();
  let milestoneCtaText = 'All milestones completed!';
  if (!allMilestonesCompleted) {
    milestoneCtaText = `${milestoneCtaLabel}: $${milestoneCtaAmount} on your ${milestoneReturnOrdinal} return`;
  }
  const earningsSummary = milestoneProgress?.earningsSummary ?? null;
  const cleanOutsSummary = earningsSummary?.cleanOuts ?? null;
  const cashBackSummary = earningsSummary?.cashBack ?? null;
  const cleanOutsSubtitle = formatCleanOutsSubtitle(asArray(cleanOutsSummary?.productCounts))
    || '0 bags | 0 boxes';
  const cleanOutsEarnedValue = toFiniteNumber(cleanOutsSummary?.totalEarned, 0);
  const cashBackEarnedValue = toFiniteNumber(cashBackSummary?.totalEarned, 0);
  const transactionsCountValue = Math.max(0, toFiniteNumber(cashBackSummary?.transactionCount, 0));
  const nextMilestoneIndex = allMilestonesCompleted
    ? lastMilestoneIndex
    : Math.min(Math.max(activeThresholdIndex, 0), lastMilestoneIndex);
  const activeMilestoneBonusAmount = toFiniteNumber(
    targetMilestoneBonusAmount
      || milestoneThresholds[nextMilestoneIndex]?.bonusAmount
      || nextMilestone?.bonusAmount
      || 0,
    0,
  );
  const clampedRangeEnd = Math.max(targetMilestoneBagNumber, segmentStartBag);
  const clampedCurrentBagInRange = Math.min(
    Math.max(currentBagNumberInCycle + 1, segmentStartBag),
    clampedRangeEnd,
  );
  const currentMarkerBagNumber = useMemo(() => {
    if (!markerBagNumbers.length) {
      return null;
    }

    return clampedCurrentBagInRange;
  }, [
    clampedCurrentBagInRange,
    markerBagNumbers,
  ]);
  const foundCurrentRangeIndex = markerBagNumbers.findIndex(
    (bagNumber) => bagNumber === currentMarkerBagNumber,
  );
  const currentRangeIndex = markerBagNumbers.length > 0
    ? Math.min(Math.max(foundCurrentRangeIndex, 0), markerBagNumbers.length - 1)
    : 0;
  const progressMarkerRatio = useMemo(() => {
    if (!markerBagNumbers.length) {
      return 0;
    }
    if (allMilestonesCompleted) {
      return 1;
    }
    if (currentBagNumberInCycle < segmentStartBag) {
      return 0;
    }

    const markerCount = markerBagNumbers.length;
    if (markerCount <= 1) {
      return 1;
    }
    const normalizedBag = Math.min(
      Math.max(currentBagNumberInCycle, segmentStartBag),
      targetMilestoneBagNumber,
    );

    let intervalIndex = 0;
    for (let index = 0; index < markerCount - 1; index += 1) {
      if (normalizedBag >= markerBagNumbers[index + 1]) {
        intervalIndex = index + 1;
      }
    }

    const intervalStartBag = markerBagNumbers[intervalIndex];
    const nextIntervalIndex = Math.min(intervalIndex + 1, markerCount - 1);
    const intervalEndBag = markerBagNumbers[nextIntervalIndex];
    const bagSpan = Math.max(intervalEndBag - intervalStartBag, 1);
    const intervalProgress = Math.min(
      1,
      Math.max(0, (normalizedBag - intervalStartBag) / bagSpan),
    );
    const markerStepRatio = 1 / (markerCount - 1);
    let ratio = (intervalIndex * markerStepRatio) + (intervalProgress * markerStepRatio);

    const shouldAdvanceHalfStep = normalizedBag >= segmentStartBag
      && normalizedBag < targetMilestoneBagNumber
      && intervalIndex < markerCount - 1;
    if (shouldAdvanceHalfStep) {
      ratio += markerStepRatio * 0.5;
    }

    return Math.min(1, Math.max(0, ratio));
  }, [
    allMilestonesCompleted,
    currentBagNumberInCycle,
    markerBagNumbers,
    segmentStartBag,
    targetMilestoneBagNumber,
  ]);
  let progressTrackWidthStyle = `min(calc(100% - 8px), max(0px, calc(${milestoneNodeCenterInsetPx}px + ((100% - ${(milestoneNodeCenterInsetPx * 2)}px) * ${progressMarkerRatio}))))`;
  if (allMilestonesCompleted) {
    progressTrackWidthStyle = 'calc(100% - 8px)';
  } else if (progressMarkerRatio <= 0) {
    progressTrackWidthStyle = '0px';
  }

  const closeDrawer = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);

  const navigateToRoute = useCallback((route, search = {}) => {
    closeDrawer();
    navigateWithSearch(route, search);
  }, [closeDrawer, navigateWithSearch]);

  const handleNavigation = (item) => {
    if (item.external) {
      window.open(item.route, '_blank', 'noopener,noreferrer');
      return;
    }

    navigateToRoute(item.route);
  };

  const handleNotificationsScroll = useCallback((event) => {
    if (!showNotificationsDrawerFeatures || !notificationsHasMore || notificationsFetching) {
      return;
    }
    const el = event.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (!nearBottom || notificationsLoadMoreLockRef.current) {
      return;
    }
    notificationsLoadMoreLockRef.current = true;
    setNotificationsPage((p) => p + 1);
  }, [showNotificationsDrawerFeatures, notificationsHasMore, notificationsFetching]);

  const notificationHasClickableDeeplink = useCallback((notification) => {
    const deeplink = getAppNotificationDeeplink(notification);
    const target = splitNotificationDeeplinkForNavigation(
      deeplink,
      typeof window !== 'undefined' ? window.location.origin : '',
    );
    return target.kind !== 'empty';
  }, []);

  const handleNotificationPress = useCallback((notification) => {
    if (!notification) {
      return;
    }

    const deeplink = getAppNotificationDeeplink(notification);
    const target = splitNotificationDeeplinkForNavigation(
      deeplink,
      typeof window !== 'undefined' ? window.location.origin : '',
    );

    if (process.env.NODE_ENV !== 'production') {
      const d = notification?.data;
      const defaultAction = d && typeof d === 'object' ? d.defaultAction : undefined;
      let notificationDataJson = null;
      try {
        notificationDataJson = d != null ? JSON.stringify(d, null, 2) : null;
      } catch {
        notificationDataJson = '[could not stringify data]';
      }
      // eslint-disable-next-line no-console
      console.log('[MyAccount notifications] tap', {
        resolvedDeeplink: deeplink || '(empty)',
        navigationTarget: target,
        isInboxOnlyDeeplink: target.kind === 'internal'
          && isNotificationInboxWebPath(target.pathname),
        notificationRow: {
          uid: notification.uid,
          messageId: notification.messageId,
          title: notification.title,
          body: notification.body,
          status: notification.status,
          createdAt: notification.createdAt,
          updatedAt: notification.updatedAt,
        },
        notificationData: d,
        notificationDataJson,
        dataKeys: d && typeof d === 'object' ? Object.keys(d) : [],
        rawDefaultAction: defaultAction,
        topLevelDeeplink: d?.deeplink,
        uri: d?.uri,
        webPushClickAction: d?.webPushClickAction,
        hint: target.kind === 'empty' || !deeplink
          ? 'No URL resolved — inspect raw `data` and Network response for /app-notifications'
          : null,
      });
    }

    const isInboxOnly = target.kind === 'internal' && isNotificationInboxWebPath(target.pathname);

    // Inbox deeplinks: stay in-app (no /notifications push — avoids redirect flash).
    if (target.kind === 'external') {
      window.open(target.href, '_blank', 'noopener,noreferrer');
    } else if (isInboxOnly && showNotificationsDrawerFeatures) {
      try {
        sessionStorage.setItem(
          ACCOUNT_DRAWER_NOTIFICATIONS_TAB_SESSION_KEY,
          DRAWER_TAB_NOTIFICATIONS,
        );
      } catch {
        /* ignore */
      }
      if (isDrawer) {
        setDrawerTab(DRAWER_TAB_NOTIFICATIONS);
      } else {
        window.dispatchEvent(new Event(ACCOUNT_DRAWER_OPEN_EVENT));
      }
    } else if (target.kind === 'internal') {
      navigateToRoute(target.pathname, target.search);
    }

    if (notification.status !== NotificationStatus.UNREAD) {
      return;
    }

    setNotificationList((prev) => prev.map((n) => (
      n.uid === notification.uid ? { ...n, status: NotificationStatus.READ } : n
    )));

    (async () => {
      try {
        await updateAppNotificationStatus(notification.messageId, NotificationStatus.READ);
        await refreshUnreadNotificationsTotal();
      } catch {
        setNotificationList((prev) => prev.map((n) => (
          n.uid === notification.uid ? { ...n, status: NotificationStatus.UNREAD } : n
        )));
      }
    })();
  }, [isDrawer, navigateToRoute, refreshUnreadNotificationsTotal, showNotificationsDrawerFeatures]);

  const handleScroll = useCallback((event) => {
    const scrollContainer = event.currentTarget;
    const scrollTop = Math.max(event.currentTarget.scrollTop ?? 0, 0);
    lastScrollTopRef.current = scrollTop;

    if (!isHeroCollapseEnabled) {
      return;
    }

    const {
      heroCollapseProgress: nextProgress,
      lockedContentOffset: nextLockedOffset,
    } = getHeroScrollState(
      scrollTop,
      HERO_COLLAPSE_SCROLL_DISTANCE,
      HERO_COLLAPSE_MAX_PROGRESS,
    );

    setHeroCollapseProgress((currentProgress) => {
      if (Math.abs(nextProgress - currentProgress) <= 0.005) {
        return currentProgress;
      }

      return nextProgress;
    });

    setLockedContentOffset((currentOffset) => {
      if (Math.abs(nextLockedOffset - currentOffset) <= 0.5) {
        return currentOffset;
      }

      return nextLockedOffset;
    });

    const reachedExpanded = scrollTop <= 1;
    const reachedCollapsed = scrollTop >= HERO_COLLAPSE_SCROLL_DISTANCE - 1;
    if (isAutoSnappingRef.current && (reachedExpanded || reachedCollapsed)) {
      isAutoSnappingRef.current = false;
      if (snapSettleTimerRef.current) {
        clearTimeout(snapSettleTimerRef.current);
        snapSettleTimerRef.current = null;
      }
      if (snapReleaseTimerRef.current) {
        clearTimeout(snapReleaseTimerRef.current);
        snapReleaseTimerRef.current = null;
      }
      return;
    }

    if (isAutoSnappingRef.current) {
      return;
    }

    if (snapSettleTimerRef.current) {
      clearTimeout(snapSettleTimerRef.current);
    }
    snapSettleTimerRef.current = window.setTimeout(() => {
      snapSettleTimerRef.current = null;
      if (isAutoSnappingRef.current) {
        return;
      }

      const currentScrollTop = Math.max(scrollContainer.scrollTop ?? 0, 0);
      const snapTarget = getHeroSnapTarget(
        currentScrollTop,
        HERO_COLLAPSE_SCROLL_DISTANCE,
      );
      if (snapTarget === null || Math.abs(snapTarget - currentScrollTop) <= 1) {
        return;
      }

      isAutoSnappingRef.current = true;
      if (snapReleaseTimerRef.current) {
        clearTimeout(snapReleaseTimerRef.current);
      }
      scrollContainer.scrollTo({
        top: snapTarget,
        behavior: 'smooth',
      });
      snapReleaseTimerRef.current = window.setTimeout(() => {
        isAutoSnappingRef.current = false;
        snapReleaseTimerRef.current = null;
      }, HERO_SNAP_RELEASE_DELAY_MS);
    }, HERO_SNAP_SETTLE_DELAY_MS);
  }, [isHeroCollapseEnabled]);

  const heroVisualProgress = isHeroCollapseEnabled
    ? Math.min(
      heroCollapseProgress / HERO_COLLAPSE_MAX_PROGRESS,
      1,
    )
    : 0;

  const renderNotificationThumb = (notification) => {
    const imageUrl = notification?.data?.imageUrl;
    const isReward = notification?.data?.type === NOTIFICATION_TYPE_REWARD;
    if (imageUrl) {
      return (
        <span
          className={`MyAccount__notification-card-thumb${isReward ? ' MyAccount__notification-card-thumb--reward' : ''}`}
        >
          <img src={imageUrl} alt="" />
        </span>
      );
    }
    const initial = (notification.title || '?').trim().charAt(0).toUpperCase();
    return (
      <span className="MyAccount__notification-card-fallback" aria-hidden="true">
        {initial}
      </span>
    );
  };

  if (isLoadingFirebaseAuth) {
    return (
      <div className={`MyAccount${isDrawer ? ' MyAccount--drawer' : ''}`}>
        <div className="MyAccount__container">
          <LoadingBar />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={accountScrollRef}
      className={`MyAccount${isDrawer ? ' MyAccount--drawer' : ''}${isDrawer && showNotificationsDrawerFeatures && drawerTab === DRAWER_TAB_NOTIFICATIONS ? ' MyAccount--drawerNotifications' : ''}`}
      onScroll={handleScroll}
    >
      <div className="MyAccount__container">
        <div className="MyAccount__contentAccount">
          {isDrawer && showNotificationsDrawerFeatures && drawerTab === DRAWER_TAB_NOTIFICATIONS ? (
            <div className="MyAccount__notifications-tab">
              <div className="MyAccount__notifications-top-bar">
                <div className="MyAccount__notifications-top-balance">
                  <img src={TCBalanceIcon} alt="" aria-hidden="true" />
                  <span>{formatCurrencyNumber(accountBalance)}</span>
                </div>
                <div className="MyAccount__notifications-top-actions">
                  <div className="MyAccount__notifications-top-bell" aria-hidden="true">
                    <img
                      className="MyAccount__notifications-top-bell-icon"
                      src={
                        hasUnreadNotifications ? NotificationsNavIconUnread : NotificationsNavIcon
                      }
                      alt=""
                    />
                  </div>
                  <PackageTrackingPill />
                </div>
              </div>
              <div className="MyAccount__notifications-header">
                <button
                  type="button"
                  className="MyAccount__notifications-back"
                  aria-label="Back to account"
                  onClick={() => setDrawerTab(DRAWER_TAB_ACCOUNT)}
                >
                  <img src={BackArrowIcon} alt="" aria-hidden="true" />
                </button>
                <h1 className="MyAccount__notifications-title">Notifications</h1>
                <div className="MyAccount__notifications-header-spacer" />
              </div>
              <div className="MyAccount__notifications-chips">
                <button
                  type="button"
                  className={`MyAccount__notifications-chip${notificationsFilter === NOTIFICATIONS_FILTER_MESSAGES ? ' is-active' : ''}`}
                  onClick={() => setNotificationsFilter(NOTIFICATIONS_FILTER_MESSAGES)}
                >
                  Messages
                </button>
                <button
                  type="button"
                  className={`MyAccount__notifications-chip${notificationsFilter === NOTIFICATIONS_FILTER_NEWS ? ' is-active' : ''}`}
                  onClick={() => setNotificationsFilter(NOTIFICATIONS_FILTER_NEWS)}
                >
                  News
                </button>
              </div>
              <div
                className="MyAccount__notifications-list"
                onScroll={handleNotificationsScroll}
                role="presentation"
              >
                {showNotificationsSkeleton && (
                  <div className="MyAccount__notifications-skeleton" aria-busy="true">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="MyAccount__notifications-skeleton-row" />
                    ))}
                  </div>
                )}
                {!showNotificationsSkeleton && showNotificationsEmpty && (
                  <div className="MyAccount__notifications-empty">
                    <div className="MyAccount__notifications-empty-icon" aria-hidden="true">
                      <img
                        className="MyAccount__notifications-empty-telescope"
                        src={TelescopeIcon}
                        alt=""
                      />
                    </div>
                    <p className="MyAccount__notifications-empty-title">You have no notifications</p>
                  </div>
                )}
                {!showNotificationsSkeleton && !showNotificationsEmpty && (
                  <div className="MyAccount__notifications-list-group">
                    {notificationsContent.unread.map((notification) => (
                      <button
                        key={`${notificationsFilter}-unread-${notification.uid}`}
                        type="button"
                        className={`MyAccount__notification-card MyAccount__notification-card--unread${notificationHasClickableDeeplink(notification) ? '' : ' MyAccount__notification-card--no-deeplink'}`}
                        onClick={() => handleNotificationPress(notification)}
                      >
                        <div className="MyAccount__notification-card-head">
                          <span className="MyAccount__notification-card-title">{notification.title}</span>
                          <span className="MyAccount__notification-card-meta">
                            <span className="MyAccount__notification-card-time">
                              {getRelativeNotificationTime(notification.createdAt)}
                            </span>
                            <span className="MyAccount__notification-card-dot" aria-hidden="true" />
                          </span>
                        </div>
                        <div className="MyAccount__notification-card-body">
                          {renderNotificationThumb(notification)}
                          <p className="MyAccount__notification-card-text">
                            {notification.body}
                          </p>
                        </div>
                      </button>
                    ))}
                    {notificationsContent.read.length > 0 && (
                      <div className="MyAccount__notifications-read-group">
                        {notificationsContent.unread.length > 0 && (
                          <div className="MyAccount__notifications-read-divider" />
                        )}
                        <p className="MyAccount__notifications-read-label">Read</p>
                        {notificationsContent.read.map((notification) => (
                          <button
                            key={`${notificationsFilter}-read-${notification.uid}`}
                            type="button"
                            className={`MyAccount__notification-card MyAccount__notification-card--read${notificationHasClickableDeeplink(notification) ? '' : ' MyAccount__notification-card--no-deeplink'}`}
                            onClick={() => handleNotificationPress(notification)}
                          >
                            <div className="MyAccount__notification-card-head">
                              <span className="MyAccount__notification-card-title">{notification.title}</span>
                              <span className="MyAccount__notification-card-meta">
                                <span className="MyAccount__notification-card-time">
                                  {getRelativeNotificationTime(notification.createdAt)}
                                </span>
                              </span>
                            </div>
                            <div className="MyAccount__notification-card-body">
                              {renderNotificationThumb(notification)}
                              <p className="MyAccount__notification-card-text">
                                {notification.body}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {notificationsFetching && filteredNotifications.length > 0 && (
                      <div className="MyAccount__notifications-load-more" aria-hidden="true" />
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div
                ref={heroContainerRef}
                className={`MyAccount__hero is-sticky${heroVisualProgress > 0 ? ' is-scroll-started' : ''}`}
                style={{ '--collapse-progress': heroVisualProgress }}
              >
                <div className="MyAccount__top-row">
                  <div className="MyAccount__top-balance">
                    <img src={TCBalanceIcon} alt="" aria-hidden="true" />
                    <span>{formatCurrencyNumber(accountBalance)}</span>
                  </div>
                  <div className="MyAccount__top-actions">
                    {isDrawer && showNotificationsDrawerFeatures && (
                      <button
                        type="button"
                        className="MyAccount__top-action MyAccount__top-action--bell"
                        aria-label="Notifications"
                        onClick={() => setDrawerTab(DRAWER_TAB_NOTIFICATIONS)}
                      >
                        <img className="MyAccount__top-action-icon MyAccount__top-action-icon--bell" src={NotificationBellIcon} alt="" aria-hidden="true" />
                        {hasUnreadNotifications && (
                          <span className="MyAccount__top-action-dot" aria-hidden="true" />
                        )}
                      </button>
                    )}
                    <button type="button" className="MyAccount__top-action" aria-label="Cart" onClick={() => navigateToRoute(ROUTE_WALLET)}>
                      <img className="MyAccount__top-action-icon MyAccount__top-action-icon--cart" src={ShoppingCartIcon} alt="" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="MyAccount__title">Account</div>
                <div className="MyAccount__hero-card">
                  <div className="MyAccount__header MyAccount__collapsible-block">
                    <div className="MyAccount__header-main">
                      <div className="MyAccount__profile-stack" aria-hidden="true">
                        <span className="MyAccount__profile-primary">{profileInitial}</span>
                        {userData?.activeSubscription ? (
                          <span className="MyAccount__profile-badge">
                            <img
                              className="MyAccount__profile-badge-icon"
                              src={UnlimitedBadgeIcon}
                              alt=""
                              aria-hidden="true"
                            />
                          </span>
                        ) : null}
                      </div>
                      <div className={`MyAccount__userTitle${isEmailFallbackUserTitle ? ' MyAccount__userTitle--email' : ''}`}>
                        {renderedUserDisplayName}
                      </div>
                    </div>
                    <div className="MyAccount__member-meta">
                      <div className="MyAccount__infoTitle">{activeMonths !== null ? `Active ${Number(activeMonths)} mos.` : 'Active member'}</div>
                      {accountStatusLabel && (
                        <div className="MyAccount__infoTitle">{accountStatusLabel}</div>
                      )}
                    </div>
                  </div>

                  <div className="MyAccount__milestones">
                    <div className="MyAccount__milestones-collapsible MyAccount__collapsible-block">
                      <div className="MyAccount__milestones-title">My Milestones & Jackpots</div>
                      <MilestoneProgressBar
                        milestoneThresholds={milestoneThresholds}
                        markerBagNumbers={markerBagNumbers}
                        allMilestonesCompleted={allMilestonesCompleted}
                        previousMilestoneBagNumber={previousMilestoneBagNumber}
                        previousMilestoneBonusAmount={previousMilestoneBonusAmount}
                        showPerBagRewardAmount={showPerBagRewardAmount}
                        currentBagNumberInCycle={currentBagNumberInCycle}
                        currentMarkerBagNumber={currentMarkerBagNumber}
                        activeMilestoneBonusAmount={activeMilestoneBonusAmount}
                        progressTrackWidthStyle={progressTrackWidthStyle}
                        currentRangeIndex={currentRangeIndex}
                      />
                    </div>
                    <button
                      type="button"
                      className="MyAccount__milestones-button"
                      onClick={() => navigateToRoute(ROUTE_TAKE_BACK_BAGS)}
                    >
                      {milestoneCtaText}
                    </button>
                  </div>

                  {earningsSummary && (
                    <div className="MyAccount__stats MyAccount__collapsible-block">
                      <div className="MyAccount__stats-header">
                        <div className="MyAccount__stats-header-title">Total Cash Back Earned</div>
                        <div className="MyAccount__stats-header-meta">Running total of your TrashieCash</div>
                      </div>
                      <button
                        type="button"
                        className="MyAccount__stats-card"
                        onClick={() => navigateToRoute(ROUTE_TAKE_BACK_BAGS)}
                      >
                        <div className="MyAccount__stats-copy">
                          <div className="MyAccount__stats-title">Take Back Earnings</div>
                          <div className="MyAccount__stats-meta">{cleanOutsSubtitle}</div>
                        </div>
                        <div className="MyAccount__stats-value">{formatCurrencyNumber(cleanOutsEarnedValue)}</div>
                      </button>
                      <button
                        type="button"
                        className="MyAccount__stats-card"
                        onClick={() => navigateToRoute(ROUTE_TRANSACTIONS)}
                      >
                        <div className="MyAccount__stats-copy">
                          <div className="MyAccount__stats-title">Deals Earnings</div>
                          <div className="MyAccount__stats-meta">{`${transactionsCountValue} ${transactionsCountValue === 1 ? 'Transaction' : 'Transactions'}`}</div>
                        </div>
                        <div className="MyAccount__stats-value">{formatCurrencyNumber(cashBackEarnedValue)}</div>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div
                className="MyAccount__body"
                style={{ transform: `translateY(${lockedContentOffset}px)` }}
              >
                <div className="MyAccount__section MyAccount__section--my-stuff">
                  <div className="MyAccount__section-title">My Stuff</div>
                  <div className="MyAccount__quick-grid">
                    {MY_STUFF_PRIMARY.map((item) => (
                      <button
                        key={item.title}
                        type="button"
                        className="MyAccount__quick-item"
                        onClick={() => handleNavigation(item)}
                      >
                        <img src={item.icon} alt="" aria-hidden="true" />
                        <span>{item.title}</span>
                      </button>
                    ))}
                  </div>

                  <div className="MyAccount__list">
                    {MY_STUFF_SECONDARY.map((item) => {
                      const IconComponent = item.iconComponent;

                      return (
                        <button
                          key={item.title}
                          type="button"
                          className="MyAccount__list-item"
                          onClick={() => handleNavigation(item)}
                        >
                          <div className="MyAccount__list-item-left">
                            {IconComponent ? (
                              <IconComponent aria-hidden="true" />
                            ) : (
                              <img src={item.icon} alt="" aria-hidden="true" />
                            )}
                            <span>{item.title}</span>
                          </div>
                          <span className="MyAccount__list-chevron">
                            <img src={CHEVRON_RIGHT_FIGMA_ICON} alt="" aria-hidden="true" />
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {userData?.activeSubscription && (
                    <>
                      <div className="MyAccount__subsection-title">Trashie Unlimited</div>
                      <div className="MyAccount__list">
                        {MEMBERSHIP_ITEMS.map((item) => {
                          const IconComponent = item.iconComponent;

                          return (
                            <button
                              key={item.title}
                              type="button"
                              className="MyAccount__list-item"
                              onClick={() => handleNavigation(item)}
                            >
                              <div className="MyAccount__list-item-left">
                                {IconComponent ? (
                                  <IconComponent aria-hidden="true" />
                                ) : (
                                  <img src={item.icon} alt="" aria-hidden="true" />
                                )}
                                <span>{item.title}</span>
                              </div>
                              <span className="MyAccount__list-chevron">
                                <img src={CHEVRON_RIGHT_FIGMA_ICON} alt="" aria-hidden="true" />
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                <div className="MyAccount__section MyAccount__impact" ref={impactCardRef}>
                  <div className="MyAccount__section-title MyAccount__section-title--with-icon">
                    <span>My Impact</span>
                    <button
                      type="button"
                      className="MyAccount__impact-share"
                      aria-label="Share my impact"
                      onClick={handleShareImpact}
                      disabled={isSharingImpact}
                    >
                      <img src={ShareIcon} alt="" aria-hidden="true" />
                    </button>
                  </div>
                  {isLoadingImpact ? (
                    <LoadingBar />
                  ) : (
                    <div className="MyAccount__impact-metrics">
                      {impactMetrics.map((metric) => {
                        const isEmptyMetric = metric.value <= 0;
                        const emptyCopy = IMPACT_EMPTY_STATE[metric.key];

                        return (
                          <div className="MyAccount__impact-item" key={metric.key}>
                            <div className="MyAccount__impact-label">
                              {isEmptyMetric ? emptyCopy.label : metric.label}
                            </div>
                            <div className={`MyAccount__impact-pill MyAccount__impact-pill--${metric.modifier}`}>
                              {isEmptyMetric ? emptyCopy.title : metric.valueDisplay}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="MyAccount__section">
                  <div className="MyAccount__section-title">Support</div>
                  <div className="MyAccount__list">
                    {[...SUPPORT_ITEMS, ...ACCOUNT_ITEMS].map((item) => (
                      <button
                        key={item.title}
                        type="button"
                        className="MyAccount__list-item"
                        onClick={() => handleNavigation(item)}
                      >
                        <div className="MyAccount__list-item-left">
                          <img src={item.icon} alt="" aria-hidden="true" />
                          <span>{item.title}</span>
                        </div>
                        {item.external && (
                          <span className="MyAccount__list-chevron">
                            <img src={EXTERNAL_LINK_FIGMA_ICON} alt="" aria-hidden="true" />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAccount;
